import { useEffect, useState } from 'react';
import { api, type ControlesPlan, type VariantesPlan } from './api';

/**
 * PLAN EXPERT (§7) — l'écran qui commande le document.
 *
 * ⚠️ LE PLAN N'EST PAS RENDU DANS DU JSON PUIS REDESSINÉ ICI. La route rend le
 * dessin : le navigateur sait l'afficher, l'imprimer À L'ÉCHELLE et
 * l'enregistrer, sans qu'une ligne de code s'interpose. Un aperçu redessiné en
 * React serait une SECONDE représentation du même plan — et c'est toujours
 * celle qu'on ne vérifie pas qui diverge de celle qu'on imprime.
 *
 * ⚠️ LES CONTRÔLES PASSENT AVANT LE BOUTON. Fondateur, 14/09/2026 : « Avant de
 * générer un document, exécuter automatiquement les contrôles. Afficher PRÊT À
 * GÉNÉRER ou GÉNÉRATION BLOQUÉE + motifs. » Un refus découvert APRÈS le clic
 * arrive trop tard : l'agent a attendu le rendu pour apprendre qu'il manquait
 * une coordonnée. Ici il le sait avant, et il sait QUOI aller chercher.
 */
export function PlanExpert({ identifiant }: { identifiant: string }) {
  const [ref, setRef] = useState<VariantesPlan | null>(null);
  const [variante, setVariante] = useState('cadastral');
  const [format, setFormat] = useState('a4_paysage');
  const [echelle, setEchelle] = useState<string>('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [controles, setControles] = useState<ControlesPlan | null>(null);
  const [chargementControles, setChargementControles] = useState(true);

  useEffect(() => {
    api.plans.variantes().then(setRef).catch((e) => setErreur(String(e?.message ?? e)));
  }, []);

  useEffect(() => {
    setChargementControles(true);
    setControles(null);
    api.plans.controles(identifiant)
      .then(setControles)
      .catch((e) => setErreur(String(e?.message ?? e)))
      .finally(() => setChargementControles(false));
  }, [identifiant]);

  /**
   * ⚠️ DEUX URL DISTINCTES, PARCE QUE CE SONT DEUX CHOSES DISTINCTES.
   * `urlApercu` rend du SVG pour l'écran ; `urlDocument` rend le PDF DocTrust,
   * seul à porter le cartouche d'État, les réserves et le QR de vérification.
   * Jusqu'au 14/09/2026 l'écran n'en construisait qu'une, SANS `apercu` : le
   * cadre `<object type="image/svg+xml">` recevait donc un PDF, un flux dont
   * il n'annonçait pas le type. Le mot « aperçu » du code désignait le
   * document, et le cadre restait blanc.
   */
  const commun = { variante, format, ...(echelle ? { echelle: Number(echelle) } : {}) };
  const urlApercu = api.plans.url(identifiant, { ...commun, apercu: true });
  const urlDocument = api.plans.url(identifiant, commun);
  const description = ref?.variantes.find((v) => v.cle === variante)?.description;
  const bloque = controles?.etat === 'bloque';

  return (
    <div className="tableau-bord">
      <header className="entete-scene">
        <h2>Plan cadastral — <code>{identifiant}</code></h2>
        <div className="actions">
          {/* ⚠️ LE BOUTON EST DÉSACTIVÉ QUAND LE CONTRÔLE BLOQUE, et il DIT
              pourquoi. Le laisser cliquable renverrait un 409 que l'agent
              lirait comme une panne du service. */}
          <a
            className={`bouton primaire${bloque ? ' desactive' : ''}`}
            href={bloque ? undefined : urlDocument}
            aria-disabled={bloque}
            title={bloque ? 'Génération bloquée — voir les motifs ci-dessous' : undefined}
            onClick={(e) => { if (bloque) e.preventDefault(); }}
            target="_blank" rel="noreferrer"
          >
            Générer le plan cadastral (PDF)
          </a>
        </div>
      </header>

      {/* ═══ CONTRÔLES AVANT GÉNÉRATION ═══ */}
      <section className="controles-plan" aria-live="polite">
        {chargementControles && <p className="mention">Contrôles en cours…</p>}

        {controles && (
          <>
            <p className={bloque ? 'erreur' : 'succes'} role={bloque ? 'alert' : undefined}>
              <strong>{bloque ? 'GÉNÉRATION BLOQUÉE' : 'PRÊT À GÉNÉRER'}</strong>
              {bloque && ` — ${controles.motifs.length} contrôle(s) non satisfait(s)`}
            </p>

            {bloque && (
              <ul className="motifs">
                {controles.motifs.map((m) => <li key={m}>{m}</li>)}
              </ul>
            )}

            {/* ⚠️ LES RÉSERVES S'AFFICHENT MÊME QUAND C'EST PRÊT. Un plan
                émissible n'est pas un plan complet : ce sont ces lignes-là qui
                seront imprimées sur le document, et l'agent doit les voir
                avant de le délivrer, pas les découvrir sur le papier. */}
            {controles.reserves.length > 0 && (
              <>
                <p className="mention">
                  Le document sera délivré, et il portera ces réserves :
                </p>
                <ul className="reserves">
                  {controles.reserves.map((r) => <li key={r}>{r}</li>)}
                </ul>
              </>
            )}

            <details className="detail-controles">
              <summary>Le détail des {controles.controles.length} contrôles</summary>
              <table className="table-controles">
                <thead>
                  <tr><th>Contrôle</th><th>Verdict</th><th>Constat</th><th>Examinés</th></tr>
                </thead>
                <tbody>
                  {controles.controles.map((c) => (
                    <tr key={c.cle} data-verdict={c.verdict} data-exigence={c.exigence}>
                      <td>{c.libelle}</td>
                      <td>
                        {/* ⚠️ « non évaluable » N'EST PAS « satisfait », et
                            l'écran ne doit pas les peindre pareil : l'un dit
                            que la règle a regardé et n'a rien trouvé à
                            redire, l'autre qu'elle n'a rien pu regarder. */}
                        {c.verdict === 'satisfait' ? 'satisfait'
                          : c.verdict === 'manquant' ? 'manquant'
                          : 'non évaluable'}
                        {c.exigence === 'bloquant' && c.verdict !== 'satisfait' && ' (bloquant)'}
                      </td>
                      <td>{c.detail}</td>
                      {/* Le témoin de population, montré et non caché : c'est
                          lui qui permet de refaire le raisonnement. */}
                      <td className="nombre">{c.candidatsExamines}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>

            <p className="mention">
              Géométrie employée : <strong>version {controles.geometrie.version}</strong>
              {controles.geometrie.depuis
                && ` en vigueur depuis le ${new Date(controles.geometrie.depuis).toLocaleDateString('fr-FR')}`}
              {controles.geometrie.empreinte && (
                <> — empreinte <code>{controles.geometrie.empreinte.slice(0, 16)}</code></>
              )}
              . Le document portera ces mentions : c'est ce qui permettra, plus tard,
              de dire de quel tracé il est la vue.
            </p>

            {controles.couchesAbsentes.length > 0 && (
              <p className="mention">
                Non figuré sur le plan, faute de donnée au registre&nbsp;:{' '}
                {controles.couchesAbsentes.join(', ')}. Leur absence sur le dessin
                ne signifie pas leur absence sur le terrain.
              </p>
            )}
          </>
        )}
      </section>

      <div className="ligne-effet">
        <label>Variante
          <select value={variante} onChange={(e) => setVariante(e.target.value)}>
            {(ref?.variantes ?? []).map((v) => (
              <option key={v.cle} value={v.cle}>{v.libelle}</option>
            ))}
          </select>
        </label>
        <label>Format
          <select value={format} onChange={(e) => setFormat(e.target.value)}>
            {(ref?.formats ?? []).map((f) => (
              <option key={f.cle} value={f.cle}>{f.libelle}</option>
            ))}
          </select>
        </label>
        <label>Échelle
          <select value={echelle} onChange={(e) => setEchelle(e.target.value)}>
            {/* ⚠️ « Ajustée » choisit dans la suite NORMALISÉE, elle ne calcule
                pas une échelle sur mesure : un plan au 1:437 ne se vérifie pas. */}
            <option value="">Ajustée (suite normalisée)</option>
            {(ref?.echelles ?? []).map((e) => <option key={e} value={e}>1:{e}</option>)}
          </select>
        </label>
      </div>

      {description && <p className="mention">{description}</p>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      <div className="apercu-plan">
        {/* `<object>` plutôt que `<img>` : le SVG garde ses unités en
            millimètres et reste sélectionnable — on peut copier une
            coordonnée du tableau au lieu de la recopier à la main. */}
        {bloque ? (
          <p className="vide">
            Aucun aperçu : la génération est bloquée. Corrigez les motifs ci-dessus,
            puis rechargez — le système ne dessine pas un plan qu'il refuse de délivrer.
          </p>
        ) : (
          <object key={urlApercu} data={urlApercu} type="image/svg+xml"
            aria-label={`Aperçu du plan de ${identifiant}`}>
            <p className="vide">
              Votre navigateur n'affiche pas l'aperçu ici.{' '}
              <a href={urlDocument} target="_blank" rel="noreferrer">Ouvrir le document</a>.
            </p>
          </object>
        )}
      </div>

      <p className="mention">
        L'aperçu ci-dessus est le <strong>dessin</strong> ; le document délivré est le
        <strong> PDF</strong>, seul à porter le cartouche d'État, les réserves et le QR
        de vérification. Un aperçu enregistré depuis l'écran n'a aucune de ces garanties —
        c'est un fichier qui s'édite dans un éditeur de texte.
      </p>
    </div>
  );
}
